import { useState, useEffect } from "react";
import { Button, Modal, Form, Input, InputNumber, Select, Table, message } from "antd";
import { DesktopOutlined, FileTextOutlined, ScheduleOutlined } from "@ant-design/icons";
import { API, graphqlOperation } from "aws-amplify";
import { errorToString } from "../../utils";
import { getSystemConfigValue } from "../../utils/systemConfig";
import { GSI_PK, onPremServerInfoFromSystemConfigs, TOUCHSTONE_PLATFORMS } from "../../utils/touchstone";
import * as mutations from "../../graphql/mutations";
import Service from "../../services";
import "./ScheduleButton.scss";
import dataCenterInfo from "../../assets/json/runpod-datacenters.json";

const { Option } = Select;

const allGpuTypes = [...new Set(dataCenterInfo.flatMap((entry) => entry.gpuAvailability.map((gpu) => gpu.gpuTypeId)))];

const PREDICTION_INTERVALS = ["5m", "15m", "20m", "30m", "40m", "45m", "1h", "80m", "90m", "2h", "4h"];

const SECURE_CLOUD = "secure";
const CLOUD_TYPES = [SECURE_CLOUD, "community", "all"];
const ANY_DATA_CENTER = "Any";

const ScheduleButton = ({ submissionRecord, onTaskScheduled }) => {
  const defaultValues = {
    gpu_type: "NVIDIA GeForce RTX 4090",
    cloud_type: "secure",
    data_center_id: "EUR-IS-1",
    num_slots: 1,
    config_file: "config/default.yaml",
    el_nigma_branch: "main",
    pred_interval: ["2h"],
    tasks_per_interval: 1,
    fetched_data_end_to: "auto",
    passthrough_args: "",
  };

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [onPremServerInfo, setOnPremServerInfo] = useState();
  const [defaultPlatform, setDefaultPlatform] = useState();
  const [form] = Form.useForm();
  const f_el_nigma_branch = Form.useWatch("el_nigma_branch", form);
  const f_config_file = Form.useWatch("config_file", form);
  const f_platform = Form.useWatch("platform", form);
  const f_gpu_type = Form.useWatch("gpu_type", form);
  const f_cloud_type = Form.useWatch("cloud_type", form);

  const systemDefaults = {
    status: "QUEUED",
    status_timestamp: Math.floor(Date.now() / 1000),
  };

  // read configuraion from SystemConfig DB
  useEffect(() => {
    (async () => {
      const systemConfigs = await Service.listSystemConfigs();
      const serverInfo = onPremServerInfoFromSystemConfigs(systemConfigs);
      setOnPremServerInfo(serverInfo);
      const defaultPlatformFromConfig = getSystemConfigValue(systemConfigs, "touchstone", "default_platform");
      const choices = serverInfo.names + TOUCHSTONE_PLATFORMS.map((p) => p.key);
      const defaultPlatform = choices.includes(defaultPlatformFromConfig) ? defaultPlatformFromConfig : "ANY";
      setDefaultPlatform(defaultPlatform);
    })();
  }, []);

  const getDataCentersForGPU = (cloudType, gpuType) => {
    if (cloudType !== SECURE_CLOUD) {
      return [ANY_DATA_CENTER];
    }
    const matches = dataCenterInfo.filter((dc) => dc.gpuAvailability.map((g) => g.gpuTypeId).includes(gpuType));
    return [ANY_DATA_CENTER, ...matches.map((dc) => dc.id)];
  };

  const createTaskParamsStructure = (submissionRecord, interval, taskIdx, formParams) => {
    // construct initial structure from defaults and flat form params. Will be refined below.
    const structure = {
      gsi_pk: GSI_PK,
      type: "TRAINING",
      submission_id: submissionRecord.submission_id,
      sources: submissionRecord.sources,
      task_id: `${Date.now()}_${interval}_${taskIdx}`, // Make task_id unique for each interval
      ...defaultValues,
      ...formParams,
      pred_interval: interval, // Override with specific interval
      ...systemDefaults,
    };

    // construct the desired_resource structure
    const platform = formParams.platform;
    const data_center_id = formParams.data_center_id === ANY_DATA_CENTER ? "" : formParams.data_center_id;
    let desired_resource;
    if (platform === "ANY") {
      desired_resource = {
        type: platform,
        // provide the runpod default parameters, in case the backend need to deploy on runpod
        gpu_type: defaultValues.gpu_type,
        cloud_type: defaultValues.cloud_type,
        data_center_id: defaultValues.data_center_id,
      };
    } else if (platform === "ANY_SERVER") {
      desired_resource = {
        type: platform,
        num_slots: defaultValues.num_slots,
      };
    } else if (platform === "RUNPOD") {
      desired_resource = {
        type: platform,
        gpu_type: formParams.gpu_type,
        cloud_type: formParams.cloud_type,
        data_center_id: data_center_id,
      };
    } else {
      desired_resource = {
        type: "SERVER",
        server: formParams.platform,
        num_slots: defaultValues.num_slots,
      };
    }
    delete structure.platform;
    delete structure.gpu_type;
    delete structure.cloud_type;
    delete structure.data_center_id;
    delete structure.server;
    delete structure.num_slots;
    delete structure.tasks_per_interval;
    structure.desired_resource = JSON.stringify(desired_resource);
    return structure;
  };

  const handleSchedule = async () => {
    let formParams;
    try {
      formParams = await form.validateFields();
    } catch (error) {
      return;
    }

    try {
      const tasksPerInterval = formParams.tasks_per_interval;
      console.log(tasksPerInterval);
      // Create tasks for each selected prediction interval
      const createTaskPromises = [];
      formParams.pred_interval.forEach((interval) => {
        for (let taskIdx = 0; taskIdx < tasksPerInterval; taskIdx++) {
          const params = createTaskParamsStructure(submissionRecord, interval, taskIdx, formParams);
          createTaskPromises.push(
            API.graphql(
              graphqlOperation(mutations.createTouchstoneTasks, {
                input: params,
              })
            )
          );
        }
      });

      await Promise.all(createTaskPromises);

      if (onTaskScheduled) {
        await onTaskScheduled();
      }
      setIsModalVisible(false);
      form.resetFields();
      message.success(`Scheduled ${createTaskPromises.length} tasks for submission ${submissionRecord.submission_id}`);
    } catch (error) {
      console.log("Schedule failed:", error);
      message.error(errorToString(error));
    }
  };

  if (!onPremServerInfo) return;

  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 13 },
  };

  const selectOptions = PREDICTION_INTERVALS.map((interval) => ({
    value: interval,
    label: interval,
  }));

  return (
    <>
      <Button type="link" icon={<ScheduleOutlined />} onClick={() => setIsModalVisible(true)}>
        Schedule
      </Button>
      <Modal
        title="Schedule Tasks"
        width={640}
        open={isModalVisible}
        onOk={handleSchedule}
        onCancel={() => {
          form.resetFields();
          setIsModalVisible(false);
        }}
      >
        <Form form={form} {...layout}>
          <Form.Item name="platform" label="Platform" preserve={false} initialValue={defaultPlatform}>
            <Select
              options={[
                {
                  label: "Platforms",
                  title: "platforms",
                  options: TOUCHSTONE_PLATFORMS.map((p) => ({ label: p.description, value: p.key })),
                },
                {
                  label: "On-prem servers",
                  title: "servers",
                  options: onPremServerInfo.names.map((p) => ({ label: onPremServerInfo.attributes[p].description, value: p })),
                },
              ]}
              suffixIcon={<OnPremServersModal form={form} onPremServerInfo={onPremServerInfo} />}
            />
          </Form.Item>
          {f_platform === "RUNPOD" && (
            <>
              <Form.Item name="gpu_type" label="GPU Type" preserve={false} initialValue={defaultValues.gpu_type}>
                <Select
                  onChange={(value) => {
                    const matches = getDataCentersForGPU(f_cloud_type, value);
                    form.setFieldsValue({ data_center_id: matches[0] });
                  }}
                >
                  {allGpuTypes.map((gpu) => (
                    <Option key={gpu} value={gpu}>
                      {gpu}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="cloud_type" label="Cloud Type" preserve={false} initialValue={defaultValues.cloud_type}>
                <Select
                  onChange={(value) => {
                    if (value !== SECURE_CLOUD) {
                      form.setFieldsValue({ data_center_id: ANY_DATA_CENTER });
                    }
                  }}
                >
                  {CLOUD_TYPES.map((cloudType) => (
                    <Option key={cloudType} value={cloudType}>
                      {cloudType}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="data_center_id" label="Data Center ID" preserve={false} initialValue={defaultValues.data_center_id}>
                <Select>
                  {getDataCentersForGPU(f_cloud_type, f_gpu_type).map((dataCenterId) => (
                    <Option key={dataCenterId} value={dataCenterId}>
                      {dataCenterId}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}
          <Form.Item
            name="tasks_per_interval"
            label="Tasks per interval"
            preserve={false}
            initialValue={defaultValues.tasks_per_interval}
            rules={[{ required: true, message: "Specify number of tasks per interval" }]}
          >
            <InputNumber min={1} max={10} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="config_file" label="Config File" preserve={false} initialValue={defaultValues.config_file}>
            <Input
              placeholder={defaultValues.config_file}
              suffix={<ViewConfigFileSource branch={f_el_nigma_branch} path={f_config_file} />}
            />
          </Form.Item>
          <Form.Item name="el_nigma_branch" label="el-nigma Branch" preserve={false} initialValue={defaultValues.el_nigma_branch}>
            <Input placeholder={defaultValues.el_nigma_branch} />
          </Form.Item>
          <Form.Item
            name="pred_interval"
            label="Prediction Intervals"
            preserve={false}
            initialValue={defaultValues.pred_interval}
            rules={[{ required: true, message: "Please select at least one prediction interval" }]}
          >
            <Select mode="multiple" placeholder="Select intervals" options={selectOptions} />
          </Form.Item>
          <Form.Item
            name="fetched_data_end_to"
            label="Fetched Data End To"
            preserve={false}
            initialValue={defaultValues.fetched_data_end_to}
          >
            <Input placeholder={"timestamp in msec, auto, or empty"} />
          </Form.Item>
          <Form.Item name="passthrough_args" label="Passthrough Args" preserve={false} initialValue={defaultValues.passthrough_args}>
            <Input className="input-monospace" placeholder="--config_override exchange_fee=0.0005" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

const ViewConfigFileSource = ({ branch, path }) => {
  const url = `https://github.com/Eon-Labs/el-nigma/blob/${branch}/${path}`;
  return (
    <Button type="link" href={url} title="View Source" disabled={!branch || !path} target="_blank" style={{ height: 0 }}>
      <FileTextOutlined />
    </Button>
  );
};

const OnPremServersModal = ({ form, onPremServerInfo }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [serverStatus, setServerStatus] = useState();

  // Determine on-prem GPU utilization
  useEffect(() => {
    (async () => {
      const activeTasks = await Service.listActiveTouchstoneTasks();
      const serversCurrentStatus = structuredClone(onPremServerInfo.attributes);
      activeTasks.forEach((task) => {
        if (!task.resource) return;
        const resource = JSON.parse(task.resource);
        if (resource.server) {
          serversCurrentStatus[resource.server].num_slots -= resource.slots.length;
        }
      });
      const data = onPremServerInfo.names.map((s) => ({
        key: s,
        description: serversCurrentStatus[s].description,
        gpu_type: serversCurrentStatus[s].gpu_type,
        num_slots: onPremServerInfo.attributes[s].num_slots,
        free_slots: serversCurrentStatus[s].num_slots,
      }));
      setServerStatus(data);
    })();
    // isModalVisible dependency added so that the data refreshes when the modal is reopened
  }, [isModalVisible, onPremServerInfo]);

  const columns = [
    {
      title: "Server",
      dataIndex: "description",
      key: "description",
      sorter: (a, b) => a.decription.localeCompare(b.decription),
    },
    {
      title: "GPU type",
      dataIndex: "gpu_type",
      key: "gpu_type",
    },
    {
      title: "Installed GPUs",
      dataIndex: "num_slots",
      key: "num_slots",
    },
    {
      title: "Free GPUs",
      dataIndex: "free_slots",
      key: "free_slots",
    },
  ];

  return (
    <>
      <Button type="link" title="On-prem servers status" onClick={() => setIsModalVisible(true)} style={{ height: 0 }}>
        <DesktopOutlined />
      </Button>
      <Modal
        title="On-prem servers status"
        width={700}
        footer={null}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
        }}
      >
        <Table
          size="small"
          columns={columns}
          dataSource={serverStatus}
          pagination={false}
          rowClassName="table-row-clickable"
          onRow={(record) => {
            return {
              onClick: () => {
                form.setFieldValue("platform", record.key);
                setIsModalVisible(false);
              },
            };
          }}
        />
      </Modal>
    </>
  );
};

export default ScheduleButton;
